
import { useState } from "react";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { useAuthentication } from "@/hooks/useAuthentication";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { PasswordInput } from "./components/PasswordInput";
import { SignUpFields } from "./components/SignUpFields";
import { AuthFormButtons } from "./components/AuthFormButtons";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";

export const AuthForm = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isSignUp, setIsSignUp] = useState(false);
  const [mobileNumber, setMobileNumber] = useState("");
  const [schoolId, setSchoolId] = useState("");
  const [role, setRole] = useState<"staff" | "teacher">("staff");
  const [isCheckingSchoolId, setIsCheckingSchoolId] = useState(false);
  const [schoolIdError, setSchoolIdError] = useState<string | null>(null);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [registeredEmail, setRegisteredEmail] = useState("");
  
  const {
    loading,
    handleSignIn,
    handleSignUp
  } = useAuthentication();
  
  const { toast } = useToast();
  
  const validateEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email.trim());
  };
  
  const checkSchoolIdExists = async (schoolId: string) => {
    setIsCheckingSchoolId(true);
    setSchoolIdError(null);
    
    try {
      console.log('Checking school ID:', schoolId);
      const { data, error } = await supabase
        .from('clients')
        .select('school_id')
        .eq('school_id', schoolId)
        .maybeSingle();
        
      if (error) {
        console.error('Error checking school ID:', error);
        return false;
      }
      
      const exists = !!data;
      console.log('School ID exists:', exists);
      
      if (!exists) {
        setSchoolIdError("Sekolah Anda belum terdaftar, mohon kontak Admin Infradigital");
      }
      
      return exists;
    } catch (error) {
      console.error('Error checking school ID:', error);
      return false;
    } finally {
      setIsCheckingSchoolId(false);
    }
  };
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const trimmedEmail = email.trim();
    if (!validateEmail(trimmedEmail)) {
      toast({
        title: "Email Tidak Valid",
        description: "Silakan masukkan alamat email yang valid",
        variant: "destructive",
        duration: 3000
      });
      return;
    }
    
    if (password.length < 6) {
      toast({
        title: "Password Terlalu Pendek",
        description: "Password harus minimal 6 karakter",
        variant: "destructive",
        duration: 3000
      });
      return;
    }
    
    try {
      if (isSignUp) {
        if (!schoolId.trim()) {
          toast({
            title: "ID Sekolah Diperlukan",
            description: "Silakan masukkan ID Sekolah",
            variant: "destructive",
            duration: 3000
          });
          return;
        }
        
        const schoolExists = await checkSchoolIdExists(schoolId.trim());
        console.log('School exists:', schoolExists);
        
        if (!schoolExists) {
          return; // Stop the registration process if school doesn't exist
        }
        
        const result = await handleSignUp(trimmedEmail, password, {
          mobile_number: mobileNumber,
          school_id: schoolId,
          role: role
        });
        
        if (result && result.success) {
          setRegisteredEmail(trimmedEmail);
          setShowSuccessModal(true);
        }
      } else {
        await handleSignIn(trimmedEmail, password);
      }
    } catch (error) {
      console.error("Auth error:", error);
      toast({
        title: "Error",
        description: "Terjadi kesalahan. Silakan coba lagi.",
        variant: "destructive",
        duration: 3000
      });
    }
  };
  
  const handleCloseSuccessModal = () => {
    setShowSuccessModal(false);
    setIsSignUp(false); // Switch back to login form
    
    // Reset form fields
    setEmail("");
    setPassword("");
    setMobileNumber("");
    setSchoolId("");
    setRole("staff");
  };
  
  return (
    <>
      <Card className="w-full max-w-md bg-transparent border-0 my-0 rounded-none drop-shadow-none shadow-none">
        <CardHeader>
          <CardTitle>{isSignUp ? "Buat akun baru" : "Masuk"}</CardTitle>
          <CardDescription>
            {isSignUp ? "Daftar untuk mengakses dashboard" : "Masuk ke akun Anda"}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Input 
                type="email" 
                placeholder="Email" 
                value={email} 
                onChange={e => setEmail(e.target.value)} 
                disabled={loading || isCheckingSchoolId} 
                required 
              />
            </div>
            
            <PasswordInput 
              password={password} 
              onChange={setPassword} 
              disabled={loading || isCheckingSchoolId} 
            />
            
            {isSignUp && (
              <SignUpFields 
                schoolId={schoolId} 
                mobileNumber={mobileNumber} 
                role={role} 
                onSchoolIdChange={setSchoolId}
                onMobileNumberChange={setMobileNumber} 
                onRoleChange={setRole} 
                disabled={loading || isCheckingSchoolId}
                schoolIdError={schoolIdError} 
              />
            )}
            
            <AuthFormButtons 
              isSignUp={isSignUp} 
              loading={loading} 
              isCheckingSchoolId={isCheckingSchoolId} 
              onToggleMode={() => {
                setIsSignUp(!isSignUp);
                setSchoolIdError(null);
              }} 
            />
          </form>
        </CardContent>
      </Card>
      
      <Dialog open={showSuccessModal} onOpenChange={setShowSuccessModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Pendaftaran Berhasil</DialogTitle>
            <DialogDescription>
              Akun {registeredEmail} telah dibuat, mohon tunggu untuk aktivasi
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="sm:justify-center">
            <Button type="button" onClick={handleCloseSuccessModal}>
              OK
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
